import {
    betslipStore,
} from '~SpectateStore';

import {
    updateSelection,
    updateSelectionBatch,
} from '~Betslip/slices/selectionDetailsSlice';

import {
    updateRecommendations,
} from '~Betslip/slices/recommendationsSlice';

import {
    selectSelectionDetails,
} from '~Betslip/selectors/selectionDetailsSelectors';

import {
    selectRecommendations,
} from '~Betslip/selectors/recommendationsSelectors';

import {
    getWsInterface,
    WS_CONNECTION_NAME,
} from '~spa/Utils/Websockets';

import { calculateAmericanPrice } from '~shared/components/sport-event/utils';

const wsInterface = getWsInterface();

export const initialiseWsHandlers = () => {
    wsInterface.requestHandler(WS_CONNECTION_NAME).registerHandler('price_update', selectionUpdateHandler, 'betslip');
    wsInterface.requestHandler(WS_CONNECTION_NAME).registerHandler('event_off', eventOffHandler, 'betslip');
};

const formatSelectionsToSubscribe = (selections) => {
    return Object.keys(selections).map(selectionId => {
        const selection = selections[selectionId];
        const {
            event_id,
            market_id,
        } = selection;

        return {
            event_id,
            market_id,
            selection_id: Number(selectionId),
        };
    });
};

export const wsSubscribe = (selections) => {
    const wsSubscribeData = formatSelectionsToSubscribe(selections);

    wsInterface.requestHandler(WS_CONNECTION_NAME).subscribe(wsSubscribeData);
};

export const wsUnsubscribe = (selections) => {
    const wsSubscribeData = formatSelectionsToSubscribe(selections);

    wsInterface.requestHandler(WS_CONNECTION_NAME).unsubscribe(wsSubscribeData);
};

const eventOffHandler = (msg) => {
    const selections = selectSelectionDetails(betslipStore.getState());
    msg.events.forEach(eventId => {
        const updatedSelectionId = Object.keys(selections).find(selectionId => {
            return selections[selectionId].event_id === eventId;
        });

        if (updatedSelectionId) {
            const updatedSelection = {
                id: updatedSelectionId,
                event_active: false,
            };
            betslipStore.dispatch(updateSelection(updatedSelection));
        }
    });
};

const selectionUpdateHandler = (msg) => {
    const selectionsInState = selectSelectionDetails(betslipStore.getState());
    const recommendationsInState = selectRecommendations(betslipStore.getState());
    const selectionsToUpdate = {};

    msg.events.forEach((event)=> {
        const eventActive = Boolean(event.active) === true && !event.suspended;
        if (event.markets.length < 1) {
            const selectionsToUpdateForEvent = selectionIdsForEventUpdate(event.id);
            selectionsToUpdateForEvent.forEach(selectionId => {
                selectionsToUpdate[selectionId] = {
                    event_active: eventActive,
                };
            });
        } else {
            event.markets.forEach((market) => {
                const marketActive = Boolean(market.active) === true && !market.suspended;
                if (Object.keys(market.selections).length < 1) {
                    const selectionsToUpdate = selectionIdsForMarketUpdate(market.id);
                    selectionsToUpdate.forEach(selectionId => {
                        selectionsToUpdate[selectionId] = {
                            event_active: eventActive,
                            market_active: marketActive,
                        };
                    });
                } else {
                    Object.keys(market.selections).forEach((id) => {
                        const selection = market.selections[id];
                        const {
                            decimal_price,
                            fraction_price,
                            sp_only,
                            tradable,
                            active,
                            suspended,
                        } = selection;
                        // american price does not come from web socket update
                        const americanPrice = calculateAmericanPrice(decimal_price);
                        // only send the parts we need for update
                        const updatedSelection = {
                            decimal_price,
                            fraction_price,
                            sp_only,
                            tradable,
                            american_price: americanPrice,
                            selection_active: active && !suspended,
                            market_active: marketActive,
                            event_active: eventActive,
                        };
                        selectionsToUpdate[id] = {...updatedSelection};
                    });
                }
            });
        }
    });

    // check if the selections that are being updated from websockets are actually in the betslip
    const selectionsToUpdateInBetslip = Object.keys(selectionsToUpdate)
        .filter(id => (selectionsInState[id]))
        .reduce((selections, selId) => ({
            ...selections,
            [selId]: selectionsToUpdate[selId],
        }), {});

    if (Object.keys(selectionsToUpdateInBetslip).length > 0) {
        betslipStore.dispatch(updateSelectionBatch(selectionsToUpdateInBetslip));
    }

    // check if the selections that are being updated from websockets are actually in the recommendations
    const recommendationsToUpdateInBetslip = Object.keys(selectionsToUpdate)
        .filter(id => recommendationsInState.find(rec => rec.id === Number(id)))
        .reduce((selections, selId) => ({
            ...selections,
            [selId]: selectionsToUpdate[selId],
        }), {});

    if (Object.keys(recommendationsToUpdateInBetslip).length > 0) {
        betslipStore.dispatch(updateRecommendations(recommendationsToUpdateInBetslip));
    }
};

const selectionIdsForEventUpdate = (eventId) => {
    const selections = selectSelectionDetails(betslipStore.getState());
    const recommendations = selectRecommendations(betslipStore.getState());

    const selectionIds = Object.keys(selections).filter(selectionId => {
        return selections[selectionId].event_id === eventId;
    });

    const recommendationIds = recommendations
        .filter(({ event_id }) => event_id === eventId)
        .map(({ id }) => id);

    return selectionIds.concat(recommendationIds);
};

const selectionIdsForMarketUpdate = (marketId) => {
    const selections = selectSelectionDetails(betslipStore.getState());
    const recommendations = selectRecommendations(betslipStore.getState());

    const selectionIds = Object.keys(selections).filter(selectionId => {
        return selections[selectionId].market_id === marketId;
    });

    const recommendationIds = recommendations
        .filter(({ market_id }) => market_id === marketId)
        .map(({ id }) => id);

    return selectionIds.concat(recommendationIds);
};
